





import { defineComponent } from '@nuxtjs/composition-api'

import Background from './Background.vue'
import BackgroundReferences from './BackgroundReferences.vue'
import Bioactivity from './Bioactivity.vue'
import CasNumber from './CasNumber.vue'
import Description from './Description.vue'
import DirectionsForUse from './DirectionsForUse.vue'
import Endotoxin from './Endotoxin.vue'
import Formulation from './Formulation.vue'
import Homologies from './Homologies.vue'
import Purity from './Purity.vue'
import MolecularFormula from './MolecularFormula.vue'
import MolecularWeight from './MolecularWeight.vue'
import ProductUsageInformation from './ProductUsageInformation.vue'
import ReagentsNotIncluded from './ReagentsNotIncluded.vue'
import Solubility from './Solubility.vue'
import SourceOrPurification from './SourceOrPurification.vue'
import SpecificitySensitivity from './SpecificitySensitivity.vue'
import Storage from './Storage.vue'
import SolutionsReagents from './SolutionsReagents.vue'
import CompanionProducts from './CompanionProducts.vue'
import FluorescentProperties from './FluorescentProperties.vue'

import { MappedProduct } from '@/types/product/productTypes'
import { DATASHEET_COMPONENTS, USAGE_INFORMATION_COMPONENTS } from '@/common/constants/datasheet'
import { PRODUCT_TYPES } from '@/common/utils/product/constants'
import {
  getAllSpecies,
  getDilutionsFromApplications,
  getEclipMessageFromApplications,
  isSignalStarSecondaryAntibodyKit,
} from '@/common/utils/product/product'

export default defineComponent({
  components: {
    Background,
    BackgroundReferences,
    Bioactivity,
    CasNumber,
    Description,
    DirectionsForUse,
    Endotoxin,
    Formulation,
    Homologies,
    Purity,
    MolecularFormula,
    MolecularWeight,
    ProductUsageInformation,
    ReagentsNotIncluded,
    Solubility,
    SourceOrPurification,
    SpecificitySensitivity,
    Storage,
    SolutionsReagents,
    CompanionProducts,
    FluorescentProperties,
  },
  props: {
    product: {
      type: Object as () => MappedProduct,
      required: true,
    },
  },
  setup({ product }) {
    const createComponentObject = (component: String) => {
      switch (component) {
        case DATASHEET_COMPONENTS.BACKGROUND: {
          return {
            name: DATASHEET_COMPONENTS.BACKGROUND,
            props: { productBackground: product.attributes.productBackground },
          }
        }
        case DATASHEET_COMPONENTS.BACKGROUND_REFERENCES: {
          return {
            name: DATASHEET_COMPONENTS.BACKGROUND_REFERENCES,
            props: { references: product.references },
          }
        }
        case DATASHEET_COMPONENTS.BIOACTIVITY: {
          return {
            name: DATASHEET_COMPONENTS.BIOACTIVITY,
            props: { bioactivity: product.attributes.activity },
          }
        }
        case DATASHEET_COMPONENTS.CAS_NUMBER: {
          return {
            name: DATASHEET_COMPONENTS.CAS_NUMBER,
            props: { casNumber: product.attributes.casNumber },
          }
        }
        case DATASHEET_COMPONENTS.DESCRIPTION: {
          return {
            name: DATASHEET_COMPONENTS.DESCRIPTION,
            props: { description: product.description },
          }
        }
        case DATASHEET_COMPONENTS.DIRECTIONS_FOR_USE: {
          return {
            name: DATASHEET_COMPONENTS.DIRECTIONS_FOR_USE,
            props: { directionsForUse: product.attributes.directionsForUse },
          }
        }
        case DATASHEET_COMPONENTS.ENDOTOXIN: {
          return {
            name: DATASHEET_COMPONENTS.ENDOTOXIN,
            props: { endotoxin: product.attributes.endotoxin },
          }
        }
        case DATASHEET_COMPONENTS.FORMULATION: {
          return {
            name: DATASHEET_COMPONENTS.FORMULATION,
            props: { formulation: product.attributes.formulation },
          }
        }
        case DATASHEET_COMPONENTS.HOMOLOGIES: {
          return {
            name: DATASHEET_COMPONENTS.HOMOLOGIES,
            props: { homologies: getAllSpecies(product.attributes.homology?.homologous) },
          }
        }
        case DATASHEET_COMPONENTS.PURITY: {
          return {
            name: DATASHEET_COMPONENTS.PURITY,
            props: { purity: product.attributes.purity },
          }
        }
        case DATASHEET_COMPONENTS.MOLECULAR_FORMULA: {
          return {
            name: DATASHEET_COMPONENTS.MOLECULAR_FORMULA,
            props: { molecularFormula: product.attributes.molecularFormula },
          }
        }
        case DATASHEET_COMPONENTS.MOLECULAR_WEIGHT: {
          return {
            name: DATASHEET_COMPONENTS.MOLECULAR_WEIGHT,
            props: {
              molecularWeight: product.attributes.molecularWeight,
              observedMolecularWeight: product.attributes.observedMolecularWeight,
              productType: product.productType,
            },
          }
        }
        case DATASHEET_COMPONENTS.PRODUCT_USAGE_INFORMATION: {
          return {
            name: DATASHEET_COMPONENTS.PRODUCT_USAGE_INFORMATION,
            props: {
              dilutions: getDilutionsFromApplications(product.attributes.lots?.lot.applications?.application),
              directionsForUse: product.attributes.directionsForUse,
              solutionsReagents: product.attributes.solutionsReagents,
              formulation: product.attributes.formulation,
              productStorage: product.attributes.productStorage,
              eclipMessage: getEclipMessageFromApplications(product.attributes.lots?.lot.applications?.application),
            },
          }
        }
        case DATASHEET_COMPONENTS.REAGENTS_NOT_INCLUDED: {
          return {
            name: DATASHEET_COMPONENTS.REAGENTS_NOT_INCLUDED,
            props: { reagentsNotIncluded: product.attributes.reagentsNotIncluded },
          }
        }
        case DATASHEET_COMPONENTS.SOLUBILITY: {
          return {
            name: DATASHEET_COMPONENTS.SOLUBILITY,
            props: { solubility: product.attributes.solubility },
          }
        }
        case DATASHEET_COMPONENTS.SOURCE_OR_PURIFICATION: {
          return {
            name: DATASHEET_COMPONENTS.SOURCE_OR_PURIFICATION,
            props: { sourcePurification: product.attributes.sourcePurification },
          }
        }
        case DATASHEET_COMPONENTS.SPECIFICITY_SENSITIVITY: {
          return {
            name: DATASHEET_COMPONENTS.SPECIFICITY_SENSITIVITY,
            props: { specificitySensitivity: product.attributes.specificitySensitivity },
          }
        }
        case DATASHEET_COMPONENTS.STORAGE: {
          return {
            name: DATASHEET_COMPONENTS.STORAGE,
            props: { storage: product.attributes.productStorage },
          }
        }
        case DATASHEET_COMPONENTS.SOLUTIONS_REAGENTS: {
          return {
            name: DATASHEET_COMPONENTS.SOLUTIONS_REAGENTS,
            props: { solutionsReagents: product.attributes.solutionsReagents },
          }
        }
        case DATASHEET_COMPONENTS.COMPANION_PRODUCTS: {
          return {
            name: DATASHEET_COMPONENTS.COMPANION_PRODUCTS,
            props: { companion: product.attributes.companionProducts?.companion },
          }
        }
        case DATASHEET_COMPONENTS.FLUORESCENT_PROPERTIES: {
          return {
            name: DATASHEET_COMPONENTS.FLUORESCENT_PROPERTIES,
            props: { fluorescentProperties: product.attributes.fluorescents?.fluorescent },
          }
        }
      }
    }
    const getComponents = () => {
      switch (product.productType) {
        case PRODUCT_TYPES.GROWTH_FACTORS_AND_CYTOKINES: {
          return USAGE_INFORMATION_COMPONENTS.GROWTH_FACTORS_AND_CYTOKINES.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.CHEMICAL_MODULATORS: {
          return USAGE_INFORMATION_COMPONENTS.CHEMICAL_MODULATORS.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.ANTIBODY_DUET:
        case PRODUCT_TYPES.ANTIBODY_SAMPLER_KIT:
        case PRODUCT_TYPES.PTM_SCAN: {
          return USAGE_INFORMATION_COMPONENTS.ANTIBODY_DUET__ANTIBODY_SAMPLER_KIT.map((val) =>
            createComponentObject(val)
          )
        }
        case PRODUCT_TYPES.ELISA_KIT:
        case PRODUCT_TYPES.FAST_SCAN_ELISA_KIT: {
          return USAGE_INFORMATION_COMPONENTS.ELISA_KIT__FAST_SCAN_ELISA_KIT.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.ELISA_ANTIBODY_PAIR: {
          return USAGE_INFORMATION_COMPONENTS.ELISA_ANTIBODY_PAIR.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.OLIGO_ANTIBODY_PAIR: {
          // SignalStar Antibody Kit is not based on the product type, but it has unique condition based on product type and specific string in name
          return isSignalStarSecondaryAntibodyKit(product)
            ? USAGE_INFORMATION_COMPONENTS.SIGNALSTAR_SECONDARY_ANTIBODY_KIT.map((val) => createComponentObject(val))
            : USAGE_INFORMATION_COMPONENTS.OLIGO_ANTIBODY_PAIR.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.MATCHED_ANTIBODY_PAIR: {
          return USAGE_INFORMATION_COMPONENTS.MATCHED_ANTIBODY_PAIR.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.PROTEIN_CONTROL: {
          return USAGE_INFORMATION_COMPONENTS.PROTEIN_CONTROL.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.CELLULAR_DYES:
        case PRODUCT_TYPES.MW_MARKER: {
          return USAGE_INFORMATION_COMPONENTS.CELLULAR_DYES.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.CELL_EXTRACTS:
        case PRODUCT_TYPES.CELL_EXTRACT_KIT: {
          return USAGE_INFORMATION_COMPONENTS.CELL_EXTRACTS.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.DETECTION_SYSTEM: {
          return USAGE_INFORMATION_COMPONENTS.DETECTION_SYSTEM.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.DETECTION_SYSTEM_KIT: {
          return USAGE_INFORMATION_COMPONENTS.DETECTION_SYSTEM_KIT.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.MW_MARKER_KIT: {
          return USAGE_INFORMATION_COMPONENTS.MW_MARKER_KIT.map((val) => createComponentObject(val))
        }
        case PRODUCT_TYPES.PROTEIN_CONTROL_KIT: {
          return USAGE_INFORMATION_COMPONENTS.PROTEIN_CONTROL_KIT.map((val) => createComponentObject(val))
        }
        default: {
          return USAGE_INFORMATION_COMPONENTS.DEFAULT.map((val) => createComponentObject(val))
        }
      }
    }
    const components = getComponents()

    return {
      components,
    }
  },
})
